import { Transform, Type } from "class-transformer";
import {
  IsNotEmpty,
  IsEnum,
  IsBoolean,
  ValidateIf,
  IsUUID,
  IsString,
  IsEmail,
  IsDateString,
  MaxLength,
  ValidateNested,
  Length,
  IsDefined,
  IsArray,
  IsISO31661Alpha2,
  IsOptional,
  IsPhoneNumber,
  Matches,
  IsPostalCode,
  MinLength,
} from "class-validator";
import { t as i18nValidationMessage } from "i18next";

import {
  ApplicationStateApplicationType,
  CorporationCustomerCorporateEntityType,
  IndividualEmploymentStatus,
  IndividualIndividualType,
  ProfileCorporateKYCEngageInActivities,
  ProfileCorporateKYCPrimaryBusiness,
  ProfileCorporateKYCRegulatedStatus,
  ProfileInvestingMonthlyCryptoDeposits,
  ProfileInvestingMonthlyCryptoInvestmentDeposit,
  ProfileInvestingMonthlyCryptoInvestmentWithdrawal,
  ProfileInvestingMonthlyCryptoWithdrawals,
  ProfileInvestingMonthlyDeposits,
  ProfileInvestingMonthlyInvestmentDeposit,
  ProfileInvestingMonthlyInvestmentWithdrawal,
  ProfileInvestingMonthlyWithdrawals,
  ProfileInvestingPrimarySourceOfFunds,
  ProfileInvestingUsdValueOfCrypto,
} from "./extracted_enums";

import "@/locales/i18n";

export class GetStatesRequest {
  @IsString()
  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @IsString()
  searchTerm: string;
}

export class GetCityRequest {
  @IsString()
  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @IsString()
  @Length(2, 3)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  stateCode: string;

  @IsString()
  searchTerm: string;
}

export class CreateApplicationRequest {
  @IsEnum(ApplicationStateApplicationType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationType: ApplicationStateApplicationType;

  @IsBoolean({ message: i18nValidationMessage("validation:INVALID_BOOLEAN") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  termsAndConditionsAccepted: string;
}

export class GetApplication {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;
}

export class GetApplications {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @ValidateIf((object) => object === undefined)
  applicationId: string | undefined;
}

export class CorporateInvestmentProfileObject {
  @IsEnum(ProfileInvestingPrimarySourceOfFunds)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  primarySourceOfFunds: string;

  @ValidateIf(
    (object) =>
      object.primarySourceOfFunds === ProfileInvestingPrimarySourceOfFunds.Other
  )
  @IsString()
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  primarySourceOfFundsDescription: string;

  @IsEnum(ProfileInvestingUsdValueOfCrypto)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  usdValueOfCrypto: string;

  @IsEnum(ProfileInvestingUsdValueOfCrypto)
  @IsOptional()
  usdValueOfFiat: string;

  @IsEnum(ProfileInvestingUsdValueOfCrypto)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  totalAssets: string;

  @IsEnum(ProfileInvestingMonthlyDeposits)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyDeposits: string;

  @IsEnum(ProfileInvestingMonthlyCryptoDeposits)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyCryptoDeposits: string;

  @IsEnum(ProfileInvestingMonthlyInvestmentDeposit)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyInvestmentDeposit: string;

  @IsEnum(ProfileInvestingMonthlyCryptoInvestmentDeposit)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyCryptoInvestmentDeposit: string;

  @IsEnum(ProfileInvestingMonthlyWithdrawals)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyWithdrawals: string;

  @IsEnum(ProfileInvestingMonthlyCryptoWithdrawals)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyCryptoWithdrawals: string;

  @IsEnum(ProfileInvestingMonthlyInvestmentWithdrawal)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyInvestmentWithdrawal: string;

  @IsEnum(ProfileInvestingMonthlyCryptoInvestmentWithdrawal)
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  monthlyCryptoInvestmentWithdrawal: string;
}

export class CorporateKycObject {
  @IsArray({
    message: i18nValidationMessage("validation:SELECT_FROM_THE_LIST"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  businessJurisdictions: string[];

  @IsArray({
    message: i18nValidationMessage("validation:SELECT_FROM_THE_LIST"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  sendJurisdictions: string[];

  @IsArray({
    message: i18nValidationMessage("validation:SELECT_FROM_THE_LIST"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  receiveJurisdictions: string[];

  @IsArray({
    message: i18nValidationMessage("validation:SELECT_FROM_THE_LIST"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  engageInActivities: string[];

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation.NOT_EMPTY") })
  primaryBusiness: ProfileCorporateKYCPrimaryBusiness;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  regulatedStatus: ProfileCorporateKYCRegulatedStatus;

  @IsArray({
    message: i18nValidationMessage("validation:SELECT_FROM_THE_LIST"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  vendorsAndCounterparties: string[];

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  isDao: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  transmitsCustomerFunds: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  primaryPurpose: string;
}

export class IndividualKycObject {
  @IsArray()
  @IsString({ each: true })
  @IsNotEmpty({
    message: i18nValidationMessage("validation:NOT_EMPTY"),
    each: true,
  })
  @IsDefined()
  fundsSendReceiveJurisdictions: string[];

  @IsArray()
  @IsEnum(ProfileCorporateKYCEngageInActivities, { each: true })
  @IsNotEmpty({
    message: i18nValidationMessage("validation:NOT_EMPTY"),
    each: true,
  })
  engageInActivities: string[];
}

export class CustomerDetailsIndividualObject {
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  firstName: string;

  @IsOptional()
  @IsString()
  @Transform(({ value }) => (value === "" ? null : value))
  middleName: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  lastName: string;

  @IsEmail({}, { message: i18nValidationMessage("validation:INVALID_EMAIL") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  emailAddress: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  nationality: string;

  @IsArray()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  citizenship: string[];

  @IsString()
  @IsPhoneNumber(null, {
    message: i18nValidationMessage("validation:INVALID_PHONE_NUMBER"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  telephoneNumber: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  taxReferenceNumber: string;

  @IsString()
  @IsOptional()
  @Transform(({ value }) => (value === "" ? null : value))
  passportNumber: string;

  @Matches(/^\d{4}-\d{2}-\d{2}$/, {
    message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
  })
  @IsDateString(
    { strict: true },
    {
      message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
    }
  )
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  dateOfBirth: string;

  @ValidateNested()
  @Type(() => CorporateInvestmentProfileObject)
  investmentProfile: CorporateInvestmentProfileObject;

  @ValidateNested()
  @Type(() => IndividualKycObject)
  kycProfile: IndividualKycObject;

  @IsEnum(IndividualEmploymentStatus)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  employmentStatus: string;

  @ValidateIf(
    (object) => object.employmentStatus === IndividualEmploymentStatus.Other
  )
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  employmentDescription: string;

  @IsOptional()
  @IsString()
  @Transform(({ value }) => (value === "" ? null : value))
  employerName: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  occupation: string;
}

export class BridgeUpdateRequest {
  @MinLength(2, { message: i18nValidationMessage("validation:INVALID_STATE") })
  @MaxLength(2, { message: i18nValidationMessage("validation:INVALID_STATE") })
  @IsString()
  state: string;

  @MinLength(9, {
    message: i18nValidationMessage(
      "validation:INVALID_MIN_CORPORATE_TAX_REFERENCE_NUMBER"
    ),
  })
  @MaxLength(9, {
    message: i18nValidationMessage(
      "validation:INVALID_MAX_CORPORATE_TAX_REFERENCE_NUMBER"
    ),
  })
  corporateTaxReferenceNumber: string;
}

export class CustomerDetailsCorporationObject {
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  registeredName: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  tradingName: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  registeredNumber: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  corporateTaxReferenceNumber: string;

  @IsEmail({}, { message: i18nValidationMessage("validation:INVALID_EMAIL") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  emailAddress: string;

  @IsString()
  @IsPhoneNumber(null, {
    message: i18nValidationMessage("validation:INVALID_PHONE_NUMBER"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  telephoneNumber: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  websiteAddress: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  stateOfIncorporation: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  countryOfIncorporation: string;

  @IsEnum(CorporationCustomerCorporateEntityType)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  corporateEntityType: string;

  @ValidateIf(
    (object) =>
      object.corporateEntityType ===
      CorporationCustomerCorporateEntityType.Other
  )
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  corporateEntityTypeDescription: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  descriptionOfBusinessNature;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @MaxLength(6)
  naics: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  naicsDescription: string;

  @Matches(/^\d{4}-\d{2}-\d{2}$/, {
    message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
  })
  @IsDateString(
    {},
    {
      message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
    }
  )
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  establishedOn: string;

  @ValidateNested()
  @Type(() => CorporateInvestmentProfileObject)
  investmentProfile: CorporateInvestmentProfileObject;

  @ValidateNested()
  @Type(() => CorporateKycObject)
  kycProfile: CorporateKycObject;
}

export class CustomerDetailsCorporationRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => CustomerDetailsCorporationObject)
  customerDetails: CustomerDetailsCorporationObject;
}

export class CustomerDetailsIndividualRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => CustomerDetailsIndividualObject)
  customerDetails: CustomerDetailsIndividualObject;
}

export class MailingAddressObject {
  @IsString()
  @IsOptional()
  @Transform(({ value }) => (value === "" ? null : value))
  unitNumber: string;

  @IsString()
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  addressLine1: string;

  @IsString()
  @IsOptional()
  @Transform(({ value }) => (value === "" ? null : value))
  addressLine2: string;

  @IsString()
  @IsOptional()
  @Transform(({ value }) => (value === "" ? null : value))
  addressLine3: string;

  @IsString()
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  city: string;

  @IsString()
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  state: string;

  @IsISO31661Alpha2({
    message: i18nValidationMessage("validation:COUNTRY_FORMAT"),
  })
  @IsDefined()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  countryCode: string;

  @ValidateIf((object) => object.countryCode !== "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  postalCode?: string;

  @ValidateIf((object) => object.countryCode === "US")
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  @IsPostalCode("US", {
    message: i18nValidationMessage("validation:INVALID_ZIP"),
  })
  postalCodeUs?: string;
}

export class MailingAddressRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => MailingAddressObject)
  mailingAddress: MailingAddressObject;
}

export class MailingAddressCorporationRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => MailingAddressObject)
  registeredAddress: MailingAddressObject;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => MailingAddressObject)
  physicalAddress: MailingAddressObject;
}

export class addIndividualObject {
  @IsEnum(IndividualIndividualType, {
    message: i18nValidationMessage("validation:INVALID_ENUM"),
    each: true,
  })
  @IsNotEmpty({
    message: i18nValidationMessage("validation:NOT_EMPTY"),
    each: true,
  })
  individualType: IndividualIndividualType[];

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  firstName: string;

  @IsString()
  @IsOptional()
  middleName: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  lastName: string;

  @IsEmail({}, { message: i18nValidationMessage("validation:INVALID_EMAIL") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  emailAddress: string;

  @IsString()
  @IsPhoneNumber(null, {
    message: i18nValidationMessage("validation:INVALID_PHONE_NUMBER"),
  })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  telephoneNumber: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  taxReferenceNumber: string;

  @IsString()
  @IsOptional()
  passportNumber: string;

  @IsString()
  @IsOptional()
  nationality: string;

  @IsOptional()
  citizenship: string[];

  @IsDateString(
    {},
    {
      message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
    }
  )
  dateOfBirth: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  percentageOwnership: string;

  @IsOptional()
  @IsString()
  @Transform(({ value }) => (value === "" ? null : value))
  title: string;

  @ValidateNested()
  @Type(() => MailingAddressObject)
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  mailingAddress: MailingAddressObject;
}

export class addIndividualRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsDefined({ message: i18nValidationMessage("validation:NOT_DEFINED") })
  @ValidateNested()
  @Type(() => addIndividualObject)
  customerDetails: addIndividualObject;
}
export class updateIndividualRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  applicationId: string;

  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  individualId: string;
}

export class uploadDocumentsRequest {
  @IsUUID("4", { message: i18nValidationMessage("validation:INVALID_UUID") })
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  documentId: string;
}

export class addIndividual {
  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  individualType: string;

  @IsString()
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  percentageOwnership: string;

  @Matches(/^\d{4}-\d{2}-\d{2}$/, {
    message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
  })
  @IsDateString(
    { strict: true },
    {
      message: i18nValidationMessage("validation:INVALID_DATE_STRING"),
    }
  )
  @IsNotEmpty({ message: i18nValidationMessage("validation:NOT_EMPTY") })
  relationshipEstablishedAt: string;
}
